import { useCallback } from 'react';
import { addRendition } from '@shared/utils/photoRendition';

/**
 * Returns a function that resolves a photo URL to its best available rendition.
 *
 * Today: always returns the `xlarge` rendition (matching the `<link rel="preload">`
 * in PreloadImages). This centralizes rendition selection so all header widgets
 * use a consistent URL that hits the browser's preload cache.
 *
 * Future: will manage progressive loading (medium → xlarge) internally,
 * returning the best available rendition at any given time. The consumer
 * just calls `getPhotoUrl(url)` and gets back a URL string — no need to
 * manage `isHighResLoaded` state or rendition logic.
 *
 * @example
 * ```tsx
 * const getPhotoUrl = useOptimizedEventPhoto();
 *
 * // In render — returns the best available rendition URL
 * <Box backgroundImage={`url(${getPhotoUrl(photo.url)})`} />
 * <CroppedImage imageUrl={getPhotoUrl(photo.url)} />
 * ```
 */
export const useOptimizedEventPhoto = (): ((photoUrl: string) => string) => {
  const getPhotoUrl = useCallback((photoUrl: string): string => {
    return addRendition({ url: photoUrl, renditionSize: 'xlarge' });
  }, []);

  return getPhotoUrl;
};
